import React, { useMemo } from "react";
import { cn } from "../../lib/utils";
import { Card, CardContent } from "./card";
import { Button } from "./button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { FaCheck, FaPhoneAlt } from "react-icons/fa";

/**
 * Rounded pricing / detail card with tooltipped checklist (Services modal layout).
 */
export function PricingCardWithFeatures({
  title,
  subtitle,
  features,
  startingLabel,
  priceDisplay,
  ctaLabel,
  onCtaClick,
  className,
}) {
  const effectiveRows = useMemo(() => features ?? [], [features]);

  return (
    <TooltipProvider delayDuration={250}>
      <Card
        className={cn(
          "flex w-full max-w-[464px] flex-col gap-6 rounded-[38px] border border-border bg-card p-[23px] text-card-foreground shadow-sm",
          className,
        )}
      >
        <div>
          <h2 className="text-xl font-semibold leading-[24px]">{title}</h2>
          <p className="text-base leading-[19px] text-muted-foreground">
            {subtitle}
          </p>
        </div>

        <CardContent className="flex flex-col gap-[25px] rounded-[33px] border border-border bg-background px-[27px] py-[30px]">
          {effectiveRows.map((f, i) => (
            <Tooltip key={`${f.label}-${i}`}>
              <TooltipTrigger asChild>
                <div className="flex cursor-pointer select-none items-center gap-3 text-muted-foreground transition-colors hover:text-foreground">
                  <FaCheck className="h-5 w-5 flex-shrink-0 text-primary" />
                  <span className="text-left text-base leading-[19px]">{f.label}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className={cn(
                  "max-w-[260px] rounded-xl border border-border bg-popover px-3 py-2 text-popover-foreground shadow-md",
                )}
              >
                <p className="text-sm leading-snug">{f.info}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </CardContent>

        <div className="flex flex-col-reverse items-center justify-between gap-4 px-[6px] sm:flex-row">
          <div className="flex flex-col text-center sm:text-start">
            <span className="text-sm text-muted-foreground">{startingLabel}</span>
            <span className="text-[34px] font-medium leading-[41px] text-foreground">
              {priceDisplay}
            </span>
          </div>
          <Button
            type="button"
            className={cn(
              "flex h-[56px] w-full min-w-[200px] max-w-[260px] items-center justify-center gap-3 rounded-[39px] text-[17px] font-medium text-white sm:h-[64px] sm:w-[220px] sm:text-[20px]",
              "border-[3px] border-blue-600 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500",
              "shadow-sm transition-all duration-150 hover:opacity-90",
            )}
            onClick={onCtaClick}
          >
            <FaPhoneAlt className="h-[20px] w-[20px]" aria-hidden />
            <span>{ctaLabel}</span>
          </Button>
        </div>
      </Card>
    </TooltipProvider>
  );
}
